import { Alert, Link, Stack, Typography } from '@mui/material'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import PageSection from 'components/common/PageSection'
import { AgentsListContainer } from 'components/configurations/agents/AgentsListContainer'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { muiTheme } from 'mui-theme'
import { useGetTeamInfo } from 'queries/orgs/use-get-team-info'
import { messageFromResponseError } from 'utils/strings'
import { formatWeekdayMonthDayYear } from 'utils/time/date'

export const AgentsPage: React.FC = () => {
  const { currentTeamId } = useAuth()

  const {
    data: teamInfo,
    error: teamInfoError,
    isFetching: isFetchingTeamInfo,
    isError: hasTeamInfoError
  } = useGetTeamInfo(currentTeamId)

  return (
    <PageSection
      errorMessage={messageFromResponseError(
        teamInfoError,
        'There was an issue while retrieving the team info. Please try again later.'
      )}
      hasError={hasTeamInfoError}
      isLoading={isFetchingTeamInfo}
    >
      {() => {
        const [team] = teamInfo ?? []

        if (!team) {
          return null
        }

        const {
          certificate_expiration_imminent: certificateExpirationImminent,
          certificate_expires_on: certificateExpiresOn
        } = team

        return (
          // TODO: useFlexGap
          <Stack sx={{ rowGap: 4 }}>
            <Stack data-testid="agent-list-page-header" sx={{ rowGap: 2 }}>
              {certificateExpirationImminent && (
                <Alert severity="error" sx={{ width: 'fit-content' }}>
                  Certificates will expire soon (
                  {formatWeekdayMonthDayYear(certificateExpiresOn)}) . Generate
                  a new certificate in{' '}
                  <Link
                    component={RouterLink}
                    to={`/settings/team/${currentTeamId}`}
                  >
                    Team Settings
                  </Link>{' '}
                  and update agents.
                </Alert>
              )}
              <Typography>
                The{' '}
                <MUIExternalLink href="https://www.gremlin.com/docs/platform-gremlin-daemon-and-agent">
                  Gremlin Agent
                  <Icon
                    css={{ marginLeft: muiTheme.spacing(0.5) }}
                    icon="arrowUpRightFromSquare"
                  />
                </MUIExternalLink>{' '}
                is an executable binary installed on a host operating system,
                container runtime, or Kubernetes cluster. It maintains a
                heartbeat connection to the Gremlin Control Plane to let Gremlin
                know that the host is active and able to receive orders, such as
                initiating a reliability test or injecting fault. Deactivating
                an agent will prevent that agent from running any experiments or
                tests.{' '}
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  <Link
                    component={RouterLink}
                    to={`/settings/team/${currentTeamId}`}
                  >
                    Certificate
                  </Link>{' '}
                  in use will expire:{' '}
                  {formatWeekdayMonthDayYear(certificateExpiresOn)}
                </Typography>
              </Typography>
            </Stack>
            <AgentsListContainer />
          </Stack>
        )
      }}
    </PageSection>
  )
}
